import { useState, useEffect, useCallback, useRef } from 'react';
import {
  LocalStorageKey,
  LocalStorageValue
} from '@/services/types';

// Hook 配置选项
interface UseLocalStorageOptions<T> {
  // 默认值
  defaultValue?: T;
  // 序列化函数
  serializer?: {
    read: (value: string) => T;
    write: (value: T) => string;
  };
  // 缓存过期时间（毫秒）
  ttl?: number;
  // 是否同步到其他标签页
  syncAcrossTabs?: boolean;
  // 错误处理函数
  onError?: (error: Error) => void;
}

// Hook 返回值
interface UseLocalStorageReturn<T> {
  // 当前值
  value: T;
  // 设置值
  setValue: (value: T | ((prevValue: T) => T)) => void;
  // 移除值
  removeValue: () => void;
  // 是否正在加载
  loading: boolean;
  // 错误信息
  error: Error | null;
  // 清除错误
  clearError: () => void;
  // 刷新值（从 localStorage 重新读取）
  refresh: () => void;
  // 检查值是否过期
  isExpired: () => boolean;
}

// 默认序列化器（保留以备将来使用）
// const defaultSerializer = {
//   read: (value: string) => {
//     try {
//       return JSON.parse(value);
//     } catch {
//       return value;
//     }
//   },
//   write: (value: any) => JSON.stringify(value)
// };

// 本地存储管理器
class LocalStorageManager {
  private static instance: LocalStorageManager;
  private listeners = new Map<string, Set<(value: any) => void>>();

  static getInstance(): LocalStorageManager {
    if (!LocalStorageManager.instance) {
      LocalStorageManager.instance = new LocalStorageManager();
    }
    return LocalStorageManager.instance;
  }

  constructor() {
    // 监听 storage 事件，实现跨标签页同步
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageChange.bind(this));
    }
  }

  private handleStorageChange(event: StorageEvent) {
    if (event.key && this.listeners.has(event.key)) {
      const callbacks = this.listeners.get(event.key);
      if (callbacks) {
        const newValue = event.newValue ? this.parseStorageValue(event.newValue) : null;
        callbacks.forEach(callback => callback(newValue));
      }
    }
  }

  private parseStorageValue(value: string): any {
    try {
      const parsed = JSON.parse(value) as LocalStorageValue;
      
      // 检查是否过期
      if (parsed.expiry && Date.now() > parsed.expiry) {
        return null;
      }
      
      return parsed.data;
    } catch {
      return value;
    }
  }

  private createStorageValue(data: any, ttl?: number): LocalStorageValue {
    return {
      data,
      timestamp: Date.now(),
      expiry: ttl ? Date.now() + ttl : undefined
    };
  }

  // 获取值
  getItem<T>(key: string, defaultValue?: T): T | null {
    try {
      if (typeof window === 'undefined') {
        return defaultValue ?? null;
      }

      const item = window.localStorage.getItem(key);
      if (item === null) {
        return defaultValue ?? null;
      }

      const parsedValue = this.parseStorageValue(item);
      return parsedValue !== null ? parsedValue : (defaultValue ?? null);
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return defaultValue ?? null;
    }
  }

  // 设置值
  setItem<T>(key: string, value: T, ttl?: number): boolean {
    try {
      if (typeof window === 'undefined') {
        return false;
      }

      const storageValue = this.createStorageValue(value, ttl);
      window.localStorage.setItem(key, JSON.stringify(storageValue));
      
      // 通知监听器
      this.notifyListeners(key, value);
      
      return true;
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
      return false;
    }
  }

  // 移除值
  removeItem(key: string): boolean {
    try {
      if (typeof window === 'undefined') {
        return false;
      }

      window.localStorage.removeItem(key);
      
      // 通知监听器
      this.notifyListeners(key, null);
      
      return true;
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
      return false;
    }
  }

  // 检查值是否过期
  isExpired(key: string): boolean {
    try {
      if (typeof window === 'undefined') {
        return true;
      }

      const item = window.localStorage.getItem(key);
      if (item === null) {
        return true;
      }

      const parsed = JSON.parse(item) as LocalStorageValue;
      return parsed.expiry ? Date.now() > parsed.expiry : false;
    } catch {
      return true;
    }
  }

  // 添加监听器
  addListener(key: string, callback: (value: any) => void): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    
    const callbacks = this.listeners.get(key)!;
    callbacks.add(callback);
    
    // 返回取消监听的函数
    return () => {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.listeners.delete(key);
      }
    };
  }

  // 通知监听器
  private notifyListeners(key: string, value: any) {
    const callbacks = this.listeners.get(key);
    if (callbacks) {
      callbacks.forEach(callback => callback(value));
    }
  }

  // 清理过期项
  cleanupExpired(): number {
    let cleanedCount = 0;
    
    try {
      if (typeof window === 'undefined') {
        return cleanedCount;
      }

      const keys = Object.keys(window.localStorage);
      
      for (const key of keys) {
        if (this.isExpired(key)) {
          this.removeItem(key);
          cleanedCount++;
        }
      }
    } catch (error) {
      console.error('Error cleaning up expired localStorage items:', error);
    }
    
    return cleanedCount;
  }

  // 获取存储使用情况
  getStorageInfo(): { used: number; available: number; total: number } {
    try {
      if (typeof window === 'undefined') {
        return { used: 0, available: 0, total: 0 };
      }

      let used = 0;
      const keys = Object.keys(window.localStorage);
      
      for (const key of keys) {
        const value = window.localStorage.getItem(key);
        if (value) {
          used += key.length + value.length;
        }
      }

      // 大多数浏览器的 localStorage 限制是 5MB
      const total = 5 * 1024 * 1024; // 5MB in bytes
      const available = total - used;

      return { used, available, total };
    } catch {
      return { used: 0, available: 0, total: 0 };
    }
  }
}

export function useLocalStorage<T>(
  key: LocalStorageKey,
  options: UseLocalStorageOptions<T> = {}
): UseLocalStorageReturn<T> {
  const {
    defaultValue,
    ttl,
    syncAcrossTabs = true,
    onError
  } = options;

  const storageManager = LocalStorageManager.getInstance();
  const mountedRef = useRef(true);
  
  // 状态管理
  const [value, setValue] = useState<T>(() => {
    try {
      const storedValue = storageManager.getItem(key, defaultValue);
      return storedValue as T;
    } catch (error) {
      onError?.(error as Error);
      return defaultValue as T;
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 安全的状态更新
  const safeSetValue = useCallback((newValue: T) => {
    if (mountedRef.current) {
      setValue(newValue);
    }
  }, []);

  const safeSetError = useCallback((newError: Error | null) => {
    if (mountedRef.current) {
      setError(newError);
      if (newError) {
        onError?.(newError);
      }
    }
  }, [onError]);

  // 设置值
  const setStoredValue = useCallback((newValue: T | ((prevValue: T) => T)) => {
    try {
      setLoading(true);
      setError(null);

      const valueToStore = typeof newValue === 'function' 
        ? (newValue as (prevValue: T) => T)(value)
        : newValue;

      const success = storageManager.setItem(key, valueToStore, ttl);
      
      if (success) {
        safeSetValue(valueToStore);
      } else {
        throw new Error('Failed to save to localStorage');
      }
    } catch (error) {
      safeSetError(error as Error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [key, value, ttl, storageManager, safeSetValue, safeSetError]);

  // 移除值
  const removeStoredValue = useCallback(() => {
    try {
      setLoading(true);
      setError(null);

      const success = storageManager.removeItem(key);
      
      if (success) {
        safeSetValue(defaultValue as T);
      } else {
        throw new Error('Failed to remove from localStorage');
      }
    } catch (error) {
      safeSetError(error as Error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [key, defaultValue, storageManager, safeSetValue, safeSetError]);

  // 刷新值
  const refresh = useCallback(() => {
    try {
      setLoading(true);
      setError(null);

      const storedValue = storageManager.getItem(key, defaultValue);
      safeSetValue(storedValue as T);
    } catch (error) {
      safeSetError(error as Error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [key, defaultValue, storageManager, safeSetValue, safeSetError]);

  // 检查是否过期
  const isExpired = useCallback(() => {
    return storageManager.isExpired(key);
  }, [key, storageManager]);

  // 清除错误
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // 跨标签页同步
  useEffect(() => {
    if (!syncAcrossTabs) return;

    const unsubscribe = storageManager.addListener(key, (newValue) => {
      if (newValue !== null) {
        safeSetValue(newValue);
      } else {
        safeSetValue(defaultValue as T);
      }
    });

    return unsubscribe;
  }, [key, syncAcrossTabs, defaultValue, storageManager, safeSetValue]);

  // 组件卸载时的清理
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    value,
    setValue: setStoredValue,
    removeValue: removeStoredValue,
    loading,
    error,
    clearError,
    refresh,
    isExpired
  };
}

// 导出存储管理器供其他地方使用
export { LocalStorageManager };

// 辅助函数：批量操作
export function useBatchLocalStorage() {
  const storageManager = LocalStorageManager.getInstance();

  const batchSet = useCallback((items: Array<{ key: LocalStorageKey; value: any; ttl?: number }>) => {
    const results: boolean[] = [];
    
    for (const item of items) {
      const success = storageManager.setItem(item.key, item.value, item.ttl);
      results.push(success);
    }
    
    return results;
  }, [storageManager]);

  const batchGet = useCallback(<T>(keys: LocalStorageKey[], defaultValue?: T): (T | null)[] => {
    return keys.map(key => storageManager.getItem(key, defaultValue));
  }, [storageManager]);

  const batchRemove = useCallback((keys: LocalStorageKey[]): boolean[] => {
    return keys.map(key => storageManager.removeItem(key));
  }, [storageManager]);

  return {
    batchSet,
    batchGet,
    batchRemove,
    cleanupExpired: storageManager.cleanupExpired.bind(storageManager),
    getStorageInfo: storageManager.getStorageInfo.bind(storageManager)
  };
}